import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import {
  Building2,
  Users,
  Briefcase,
  CheckCircle,
  XCircle,
  FileText,
  Smartphone,
  Bell,
  Activity,
  ArrowRight,
} from "lucide-react";
import SchoolStatsChart from "./SchoolStatsChart";
import GrowthBadges from "./GrowthBadges";

const statCards = [
  { key: "schools", label: "Customers", sub: "Accounts", icon: Building2, color: "blue", onClick: true },
  { key: "totalUsers", label: "Total Users", sub: "Across platform", icon: Users, color: "emerald" },
  { key: "totalStaff", label: "Staff", sub: "Active", icon: Briefcase, color: "slate" },
  { key: "activeSchools", label: "Active Customers", sub: "Can sign in", icon: CheckCircle, color: "green" },
  { key: "inactiveSchools", label: "Inactive", sub: "Disabled", icon: XCircle, color: "red" },
];

const quickActions = [
  { id: "schools", label: "Manage Customers", icon: Building2 },
  { id: "app-version", label: "App Version", icon: Smartphone },
  { id: "logs", label: "System Logs", icon: FileText },
];

const iconBg = {
  blue: "bg-blue-500/10 text-blue-600",
  emerald: "bg-emerald-500/10 text-emerald-600",
  violet: "bg-violet-500/10 text-violet-600",
  amber: "bg-amber-500/10 text-amber-600",
  slate: "bg-slate-500/10 text-slate-600",
  green: "bg-green-500/10 text-green-600",
  red: "bg-red-500/10 text-red-600",
};

export default function SuperAdminDashboardContent({
  schools = [],
  userStats = {},
  trends = null,
  onNavigate,
}) {
  const activeSchools = schools.filter((s) => s.isActive !== false).length;
  const inactiveSchools = schools.length - activeSchools;

  const statValues = {
    schools: schools.length,
    totalUsers: userStats.totalUsers ?? 0,
    totalStaff: userStats.totalStaff ?? 0,
    activeSchools,
    inactiveSchools,
  };

  return (
    <div className="min-h-screen p-0">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Dashboard Overview
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Platform-wide statistics and all customer data
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {quickActions.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => onNavigate?.(id === "logs" ? "logs" : id)}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-blue-200 hover:bg-blue-50/50 hover:text-blue-700"
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {statCards.map(({ key, label, sub, icon: Icon, color, onClick }) => (
            <Card
              key={key}
              className={`overflow-hidden rounded-2xl border-gray-100 bg-white shadow-sm transition-all duration-200 hover:shadow-md ${onClick ? "cursor-pointer hover:border-blue-100" : ""}`}
              onClick={onClick ? () => onNavigate?.("schools") : undefined}
            >
              <CardHeader className="pb-1 pt-5">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${iconBg[color]}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  {onClick && (
                    <ArrowRight className="h-4 w-4 text-gray-300" />
                  )}
                </div>
                <CardTitle className="mt-3 text-sm font-semibold text-gray-600">
                  {label}
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">{sub}</CardDescription>
              </CardHeader>
              <CardContent className="pb-5">
                <p className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  {typeof statValues[key] === "number" && key !== "schools"
                    ? statValues[key].toLocaleString()
                    : statValues[key]}
                </p>
              </CardContent>
            </Card>
          ))}
        </section>

        <GrowthBadges trends={trends} />

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="overflow-hidden rounded-2xl border-gray-100 bg-white shadow-sm lg:col-span-2">
            <CardHeader className="border-b border-gray-100/80 bg-gray-50/50 pb-4">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10">
                  <Activity className="h-4 w-4 text-blue-600" />
                </span>
                Users per Customer
              </CardTitle>
              <CardDescription className="text-xs text-gray-500">
                Staff by customer account
              </CardDescription>
            </CardHeader>
            <CardContent className="p-5">
              <SchoolStatsChart schools={schools} />
            </CardContent>
          </Card>

          <Card className="overflow-hidden rounded-2xl border-gray-100 bg-white shadow-sm">
            <CardHeader className="border-b border-gray-100/80 bg-gray-50/50 pb-4">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10">
                  <Bell className="h-4 w-4 text-indigo-600" />
                </span>
                Recent Customers
              </CardTitle>
              <CardDescription className="text-xs text-gray-500">Latest customer accounts</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-72 overflow-y-auto">
                {schools.length > 0 ? (
                  schools.slice(0, 6).map((school) => (
                    <div
                      key={school._id}
                      className="flex items-center justify-between gap-3 border-b border-gray-100/80 px-5 py-3 last:border-0 hover:bg-gray-50/50"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${school.isActive !== false ? "bg-green-500/10" : "bg-gray-100"}`}
                        >
                          <Building2
                            className={`h-4 w-4 ${school.isActive !== false ? "text-green-600" : "text-gray-500"}`}
                          />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{school.name}</p>
                          <p className="truncate text-xs text-gray-500">{school.domain}</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${school.isActive !== false ? "bg-green-500/10 text-green-700" : "bg-gray-200 text-gray-600"}`}
                        >
                          {school.isActive !== false ? "Active" : "Inactive"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {(school.stats?.totalUsers ?? school.userCount ?? 0)} users
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Building2 className="h-10 w-10 opacity-40" />
                    <p className="mt-2 text-sm">No customers yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        <Card className="overflow-hidden rounded-2xl border-gray-100 bg-white shadow-sm">
          <CardHeader className="border-b border-gray-100/80 bg-gray-50/50">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10">
                <Building2 className="h-4 w-4 text-blue-600" />
              </span>
              All Customers Data
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Complete list with user counts and status
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Customer
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Domain
                    </th>
                    <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Staff
                    </th>
                    <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Total
                    </th>
                    <th className="px-5 py-3.5 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schools.map((school) => (
                    <tr
                      key={school._id}
                      className="border-t border-gray-100 transition-colors hover:bg-gray-50/50"
                    >
                      <td className="px-5 py-3 font-medium text-gray-900">{school.name}</td>
                      <td className="px-5 py-3 text-gray-600">{school.domain}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-gray-700">
                        {school.stats?.totalStaff ?? "–"}
                      </td>
                      <td className="px-5 py-3 text-right font-medium tabular-nums text-gray-900">
                        {school.stats?.totalUsers ?? school.userCount ?? "–"}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${school.isActive !== false ? "bg-green-500/10 text-green-700" : "bg-gray-200 text-gray-600"}`}
                        >
                          {school.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {schools.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <Building2 className="h-12 w-12 text-gray-300" />
                  <p className="mt-3 text-sm">No customer data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
